import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Slider from "@mui/material/Slider";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import LinearProgress from "@mui/material/LinearProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ShareIcon from "@mui/icons-material/Share";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { GameHeader } from "./WordyVerse/Components/GameBoard/GameHeader";
import { styled } from "@mui/material/styles";
import { ColorMatcherShareModal } from "./ColorMatcher/ColorMatcherShareModal";

// Styled components
const GameContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(2),
  backgroundColor: "#FFFFFF",
  minHeight: "100vh",
}));

const GameCard = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: 600,
  margin: "0 auto",
  backgroundColor: "#FFFFFF",
  boxShadow: "none",
}));

const StatsBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  marginBottom: theme.spacing(4),
  "& .MuiTypography-root": {
    fontSize: "1.25rem",
    color: "#000000",
  },
}));

const ColorDisplay = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(4),
  gap: theme.spacing(2),
}));

const ColorBox = styled(Box)(({ theme }) => ({
  width: "48%",
  borderRadius: 8,
  overflow: "hidden",
  border: "2px solid #000000",
  backgroundColor: "#FFFFFF",
  boxShadow: "4px 4px 0px rgba(0, 0, 0, 0.2)",
}));

const Controls = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  width: "100%",
}));

const ColorMatcher = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getRandomColor = () => ({
    r: Math.floor(Math.random() * 256),
    g: Math.floor(Math.random() * 256),
    b: Math.floor(Math.random() * 256),
  });

  const [gameOver, setGameOver] = useState(false);
  const [targetColor, setTargetColor] = useState(getRandomColor());
  const [playerColor, setPlayerColor] = useState({ r: 128, g: 128, b: 128 });
  const [showPulse, setShowPulse] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentDate] = useState(new Date());

  const calculateMatchPercentage = (color1, color2) => {
    const maxDiff = Math.sqrt(3 * Math.pow(255, 2));
    const currentDiff = Math.sqrt(
      Math.pow(color1.r - color2.r, 2) +
        Math.pow(color1.g - color2.g, 2) +
        Math.pow(color1.b - color2.b, 2)
    );
    return Math.round((1 - currentDiff / maxDiff) * 100);
  };

  const handleColorChange = (color, value) => {
    if (!gameOver) {
      setPlayerColor((prevColor) => ({
        ...prevColor,
        [color]: parseInt(value),
      }));
    }
  };

  const handleCheckMatch = () => {
    const matchPercentage = calculateMatchPercentage(targetColor, playerColor);
    setFinalScore(matchPercentage);
    setGameOver(true);
    setShowShareModal(true);

    // Visual and haptic feedback
    setShowPulse(true);
    setTimeout(() => setShowPulse(false), 300);

    if (matchPercentage > 90 && navigator.vibrate) {
      navigator.vibrate(200);
    }
  };

  const handleShare = () => {
    const formattedDate = currentDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const shareText = `🎨 Color Matcher - ${formattedDate}\nAccuracy: ${finalScore}%\nPlay at: ${window.location.href}`;

    navigator.clipboard.writeText(shareText).catch((error) => {
      console.error("Failed to copy text: ", error);
      alert("Failed to copy score. Please try again.");
    });
  };

  return (
    <GameContainer>
      <GameCard>
        <CardContent>
          <GameHeader
            bannerPath="/assets/customer-logos/banner/comet-chronicles.svg"
            title="Color Matcher"
          />

          <Typography
            variant="h2"
            component="h1"
            align="center"
            sx={{
              fontSize: "2.75rem",
              fontWeight: "bold",
              mb: 4,
              color: "#000000",
            }}
          >
            Color Matcher
          </Typography>

          {finalScore !== null && (
            <Box
              sx={{
                mb: 4,
                textAlign: "center",
                p: 2,
                backgroundColor: "#F0F0F0",
                borderRadius: 2,
                border: "2px solid #000000",
                boxShadow: "4px 4px 0px rgba(0, 0, 0, 0.2)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.5rem",
                  color: "#000000",
                  fontWeight: "bold",
                }}
              >
                Match Accuracy: {finalScore}%
              </Typography>
            </Box>
          )}

          <ColorDisplay>
            {["Target", "Your Color"].map((label, index) => (
              <ColorBox
                key={label}
                sx={{
                  transform:
                    showPulse && index === 1 ? "scale(1.02)" : "scale(1)",
                }}
              >
                <Typography
                  sx={{
                    py: 1,
                    px: 2,
                    fontWeight: "500",
                    fontSize: "1rem",
                    backgroundColor: "#F0F0F0",
                    color: "#000000",
                    borderBottom: "2px solid #000000",
                  }}
                >
                  {label}
                </Typography>
                <Box
                  sx={{
                    height: 120,
                    bgcolor:
                      index === 0
                        ? `rgb(${targetColor.r},${targetColor.g},${targetColor.b})`
                        : `rgb(${playerColor.r},${playerColor.g},${playerColor.b})`,
                    transition: "background-color 0.3s ease",
                  }}
                />
              </ColorBox>
            ))}
          </ColorDisplay>

          <Box sx={{ mb: 4 }}>
            {[
              { label: "R", key: "r", color: "#ff6b6b", bgColor: "#FFF5F5" },
              { label: "G", key: "g", color: "#51cf66", bgColor: "#F5FFF5" },
              { label: "B", key: "b", color: "#339af0", bgColor: "#F5F5FF" },
            ].map(({ label, key, color, bgColor }) => (
              <Box
                key={key}
                sx={{ mb: 2, display: "flex", alignItems: "center" }}
              >
                <Typography
                  sx={{
                    fontWeight: "500",
                    fontSize: "1.1rem",
                    width: "25px",
                    mr: 2,
                    color: "#000000",
                  }}
                >
                  {label}
                </Typography>
                <Slider
                  value={playerColor[key]}
                  onChange={(_, newValue) => handleColorChange(key, newValue)}
                  min={0}
                  max={255}
                  disabled={gameOver}
                  sx={{
                    color,
                    height: 8,
                    padding: "15px 0",
                    "& .MuiSlider-thumb": {
                      width: 24,
                      height: 24,
                      backgroundColor: "#FFFFFF",
                      border: "2px solid #000000",
                      boxShadow: "2px 2px 0px rgba(0, 0, 0, 0.2)",
                      "&:hover, &.Mui-focusVisible": {
                        boxShadow: "3px 3px 0px rgba(0, 0, 0, 0.2)",
                      },
                    },
                    "& .MuiSlider-track": {
                      height: 8,
                      border: "2px solid #000000",
                    },
                    "& .MuiSlider-rail": {
                      height: 8,
                      backgroundColor: bgColor,
                      border: "2px solid #000000",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>

          <Controls>
            {!gameOver ? (
              <Button
                variant="contained"
                onClick={handleCheckMatch}
                sx={{
                  fontSize: "1.2rem",
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  backgroundColor: "#FFFFFF",
                  color: "#000000",
                  border: "2px solid #000000",
                  boxShadow: "4px 4px 0px rgba(0, 0, 0, 0.2)",
                  "&:hover": {
                    backgroundColor: "#F0F0F0",
                    boxShadow: "2px 2px 0px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                SUBMIT MATCH
              </Button>
            ) : null}
          </Controls>
        </CardContent>
      </GameCard>

      <ColorMatcherShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleShare}
        matchPercentage={finalScore}
        date={currentDate.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })}
      />
    </GameContainer>
  );
};

export default ColorMatcher;
